/*
 * licensePools.ts
 * Helpers to determine from which license pool a role takes its seat,
 * mirroring the backend logic in users/seats.py. Seat types the package
 * does not sell separately count as full licenses.
 */

// The seat usage meta data of the user list
export interface SeatUsageType {
    active_seats: number;
    total_seats: number;
    is_trial: boolean | null;
    has_unlimited_seats: boolean;
    enable_field_seats?: boolean;
    enable_light_seats?: boolean;
    license_pools?: Record<string, { active: number; total: number }>;
    available_license_pools?: string[];
}

// Returns the license pool a role takes a seat from, subcontractors take none
export const getRoleLicensePool = (
    role: { access_to?: string[] | null; license_type?: string | null; is_subcontractor_role?: boolean },
    seatUsage: SeatUsageType
): string | null => {
    if (role.is_subcontractor_role) return null;

    // Light roles take a light seat when the package sells them separately
    const accessTo = role.access_to || [];
    if (role.license_type === 'light_license') {
        return seatUsage.enable_light_seats ? 'light' : 'full';
    }

    // Field only roles take a field seat when the package sells them separately
    if (accessTo.includes('field') && !accessTo.includes('office')) {
        return seatUsage.enable_field_seats ? 'field' : 'full';
    }

    return 'full';
};

// Returns whether the seat limit of the environment leaves no room for any new user
export const isSeatLimitReached = (seatUsage: SeatUsageType | null, hasUnlimitedSeats: boolean): boolean => {
    if (!seatUsage || hasUnlimitedSeats || seatUsage.has_unlimited_seats || seatUsage.is_trial !== false) {
        return false;
    }

    // Use the license pools when the backend provides them
    if (seatUsage.available_license_pools) {
        return seatUsage.available_license_pools.length === 0;
    }

    return seatUsage.active_seats >= seatUsage.total_seats;
};
